import { z } from 'zod'

import { HomeworkSchema } from '~/@entities/Homework'
import {
  EssayProblemSchema,
  type Problem,
  ProblemSchema,
  ProblemSourceTypeSchema,
  type TaggedProblem,
  TaggedProblemSchema,
} from '~/@entities/Problem'
import { type ScoringItem, ScoringResultSchema } from '~/@entities/Scoring'
import { StudentIdSchema } from '~/@entities/Student'
import {
  LearningStatusSchema,
  SelfLearningWorksheetSchema,
  type WorksheetProblem,
  WorksheetProblemSchema,
  WorksheetSchema,
} from '~/@entities/Worksheet'

/***************** 챌린지 트랙 *****************/
export const CHALLENGE_TRACK = ['GENERAL', 'ARITHMETIC'] as const

/***************** 챌린지 기준 종류 *****************/
export const CHALLENGE_REFERENCE_TYPE = ['CONCEPT_CHIP', 'LITTLE_CHAPTER'] as const

/***************** 자기주도학습 단계 *****************/
export const SELF_LEARNING_STEP = [
  'BACKLOG',
  'STEP_1',
  'STEP_2',
  'STEP_3',
  'STEP_4',
  'RESOLVED',
  'DEPRECATED',
] as const

/***************** QR 학습지 응시 가능 여부 *****************/
export const WORKSHEET_VALIDATION_STATE = ['AVAILABLE', 'UNAVAILABLE', 'NOT_ASSIGN'] as const

export const ChallengeTrackSchema = z.enum(CHALLENGE_TRACK)
export type ChallengeTrack = z.infer<typeof ChallengeTrackSchema>

export const ChallengeReferenceTypeSchema = z.enum(CHALLENGE_REFERENCE_TYPE)
export type ChallengeReferenceType = z.infer<typeof ChallengeReferenceTypeSchema>

export const SelfLearningStepSchema = z.enum(SELF_LEARNING_STEP)
export type SelfLearningStep = z.infer<typeof SelfLearningStepSchema>

export const WorksheetValidationStateSchema = z.enum(WORKSHEET_VALIDATION_STATE)
export type WorksheetValidationState = z.infer<typeof WorksheetValidationStateSchema>

// 챌린지 트랙은 조회가 track, 생성이 arithmeticOnly 로 같은 축을 두 가지로 표현한다.
export const challengeTrackFromArithmeticOnly = (arithmeticOnly: boolean): ChallengeTrack =>
  arithmeticOnly ? 'ARITHMETIC' : 'GENERAL'
export const isArithmeticTrack = (track: ChallengeTrack | undefined) => track === 'ARITHMETIC'

// 서버는 Asia/Seoul LocalDateTime 을 타임존 없이 낸다.
const _LocalDateTimeSchema = z.iso.datetime({ local: true })

/** 학생에게 배정된 학습지 한 건의 진행 기록. 학습지 본체는 갖지 않는다 */
export const StudentWorksheetSchema = z.object({
  id: z.number(),
  studentHomeworkId: z.number().nullable(),
  assignDatetime: _LocalDateTimeSchema,
  openDatetime: _LocalDateTimeSchema.nullable(),
  scoreDatetime: _LocalDateTimeSchema.nullable(),
  correctCount: z.number(),
  wrongCount: z.number(),
  // 서술형 학습지는 채점이 끝나기 전까지 점수가 없다.
  score: z.number().nullable(),
  status: LearningStatusSchema,
  // 서술형 채점 결과의 학생 공개 여부. 비서술형은 null 이고, null 이 아닌 것이 곧 서술형 판별 신호다.
  essayScoringOpened: z.boolean().nullable(),
  // creverseShared(크레버스 보고서 공유 여부)는 학생 앱이 읽지 않아 선생님 앱의 AssignedWorksheetDetailSchema 만 받는다.
})
export type StudentWorksheet = z.infer<typeof StudentWorksheetSchema>

/** 배정 기록에 학습지 본체가 붙은 것. 학생 앱의 학습지 조회 응답이 이 모양이다 */
export const AssignedWorksheetSchema = StudentWorksheetSchema.extend({
  worksheet: WorksheetSchema,
})
export type AssignedWorksheet = z.infer<typeof AssignedWorksheetSchema>

/** 자기주도학습(심화·오답)으로 파생된 학습지의 배정 기록 */
export const SelfLearningAssignedWorksheetSchema = StudentWorksheetSchema.extend({
  worksheet: SelfLearningWorksheetSchema,
})
export type SelfLearningAssignedWorksheet = z.infer<typeof SelfLearningAssignedWorksheetSchema>

/** 학생 학습지 목록의 한 줄 — 숙제와 그 학습지에서 파생된 자기주도 학습지를 함께 실어 온다 */
export const AssignedWorksheetWithHomeworksSchema = AssignedWorksheetSchema.extend({
  homeworks: z.array(HomeworkSchema),
  selfLearningStudentWorksheets: z.array(SelfLearningAssignedWorksheetSchema),
})
export type AssignedWorksheetWithHomeworks = z.infer<typeof AssignedWorksheetWithHomeworksSchema>

/** 챌린지 학습지의 배정 기록. 어느 기준(유형칩·소단원)에서 만들어졌고 몇 단계까지 왔는지를 더 갖는다 */
export const ChallengeWorksheetSchema = AssignedWorksheetSchema.extend({
  referenceType: ChallengeReferenceTypeSchema,
  referenceId: z.number(),
  learningStep: SelfLearningStepSchema,
})
export type ChallengeWorksheet = z.infer<typeof ChallengeWorksheetSchema>

/** 자기주도 학습지 하나를 기준으로 본 부모 학습지와 형제 학습지들 */
export const SelfLearningWorksheetRelationsWireSchema = z.object({
  prevStudentWorksheetAndWorksheet: AssignedWorksheetSchema,
  nextStudentWorksheetAndWorksheets: z.array(AssignedWorksheetSchema),
})
export type SelfLearningWorksheetRelations = {
  prevStudentWorksheetAndWorksheet: AssignedWorksheet
  nextStudentWorksheetAndWorksheets: SelfLearningAssignedWorksheet[]
}

/** 학습지 출제가 만들어 낸 배정 한 건. 앱이 쓰는 넷만 받고 점수·숙제·서술형 공개 여부는 받지 않는다 */
export const WorksheetAssignmentSchema = z.object({
  studentWorksheetId: z.number(),
  studentId: StudentIdSchema,
  studentName: z.string(),
  status: LearningStatusSchema,
})
export type WorksheetAssignment = z.infer<typeof WorksheetAssignmentSchema>

/** 출제 요청의 응답. 후보 문항이 없으면 assignList 가 빈 배열로 온다 */
export const WorksheetAssignEchoSchema = z.object({
  assignDatetime: _LocalDateTimeSchema,
  assignList: z.array(WorksheetAssignmentSchema),
})
export type WorksheetAssignEcho = z.infer<typeof WorksheetAssignEchoSchema>

/** QR 학습지를 이 학생이 지금 풀 수 있는지. 이미 배정돼 있으면 그 배정 id 를 함께 준다 */
export const WorksheetValidationSchema = z.object({
  worksheetState: WorksheetValidationStateSchema,
  studentWorksheetId: z.number().nullable(),
})
export type WorksheetValidation = z.infer<typeof WorksheetValidationSchema>

/** 채점 전 임시 저장한 답 한 건. 다중 셀 답의 미입력 칸은 null 원소다 */
export const VirtualScoreSchema = z.object({
  worksheetProblemId: z.number(),
  result: ScoringResultSchema,
  userAnswer: z.string(),
  userAnswers: z.array(z.string().nullable()).nullable(),
})
export type VirtualScore = z.infer<typeof VirtualScoreSchema>

export const StudentWorksheetVirtualScoringSchema = z.object({
  studentWorksheetId: z.number(),
  virtualScores: z.array(VirtualScoreSchema),
})
export type StudentWorksheetVirtualScoring = z.infer<typeof StudentWorksheetVirtualScoringSchema>

/***************** 선생님 앱의 출제 기록 *****************/

/** 선생님 앱이 보는 배정 기록. 원클릭 보고서 공유 여부는 크레버스 학원만 값이 있고 그 밖은 null 이다 */
export const AssignedWorksheetDetailSchema = AssignedWorksheetSchema.extend({
  creverseShared: z.boolean().nullable(),
})
export type AssignedWorksheetDetail = z.infer<typeof AssignedWorksheetDetailSchema>

/** 학년·반에 함께 출제된 학습지의 학생 한 명. 서술형 학습지는 score 가 서술형 총점이다 */
export const GroupWorksheetAssigneeSchema = z.object({
  studentWorksheetId: StudentWorksheetSchema.shape.id,
  studentHomeworkId: StudentWorksheetSchema.shape.studentHomeworkId,
  studentId: StudentIdSchema,
  studentName: z.string(),
  score: z.number(),
  status: LearningStatusSchema,
  essayScoringOpened: StudentWorksheetSchema.shape.essayScoringOpened,
})
export type GroupWorksheetAssignee = z.infer<typeof GroupWorksheetAssigneeSchema>

/** 학년·반 학습지 목록의 한 줄 — 학습지 하나와 그것을 받은 학생들 */
export const GroupAssignedWorksheetSchema = z.object({
  worksheet: WorksheetSchema,
  assignedStudentList: z.array(GroupWorksheetAssigneeSchema),
  score: z.number().nullable(),
  status: LearningStatusSchema,
  assignDatetime: _LocalDateTimeSchema,
})
export type GroupAssignedWorksheet = z.infer<typeof GroupAssignedWorksheetSchema>

/** 학년·반에 출제된 학습지의 학생 한 명의 문항별 채점 결과 */
export const GroupWorksheetScoringSchema = z.object({
  studentId: StudentIdSchema,
  studentName: z.string(),
  studentWorksheetId: StudentWorksheetSchema.shape.id,
  score: z.number().nullable(),
  status: LearningStatusSchema,
  scoringList: z.array(
    z.object({
      worksheetProblemId: WorksheetProblemSchema.shape.id,
      result: ScoringResultSchema,
      problem: z.object({
        id: ProblemSchema.shape.id,
        sourceType: ProblemSourceTypeSchema,
      }),
    }),
  ),
})
export type GroupWorksheetScoring = z.infer<typeof GroupWorksheetScoringSchema>

/** 선생님이 학습지를 출제한 응답. 학년·반 목록의 학생 줄과 같은 모양으로 배정 결과를 싣는다 */
export const GroupWorksheetAssignEchoSchema = WorksheetAssignEchoSchema.extend({
  assignList: z.array(GroupWorksheetAssigneeSchema),
})
export type GroupWorksheetAssignEcho = z.infer<typeof GroupWorksheetAssignEchoSchema>

/***************** 문제와 채점 *****************/

/** 배정된 학습지의 문항 하나 — 문제와 그 학생의 채점 기록. conceptHidden 은 앱이 읽지 않아 받지 않는다 */
export const AssignedWorksheetProblemSchema = z.object({
  worksheetProblemId: WorksheetProblemSchema.shape.id,
  result: ScoringResultSchema,
  userAnswer: z.string(),
  // 다중 셀 답의 미입력 칸은 null 원소다. 단답 문항은 null
  userAnswers: z.array(z.string().nullable()).nullable(),
  problem: TaggedProblemSchema,
  handwrittenNoteUrl: z.string().nullable(),
  // 서술형 학습지의 문항 배점. 그 밖은 null
  score: z.number().nullable(),
})
export type AssignedWorksheetProblem = z.infer<typeof AssignedWorksheetProblemSchema>

/** 배정된 학습지 문항 목록의 한 줄 — 서술형 문항은 서술형 블록을 더 싣고, 그 밖은 null 이다 */
export const AssignedWorksheetProblemWithEssaySchema = AssignedWorksheetProblemSchema.extend({
  essay: EssayProblemSchema.nullable(),
})
export type AssignedWorksheetProblemWithEssay = z.infer<
  typeof AssignedWorksheetProblemWithEssaySchema
>

/**
 * 선생님 앱이 보는 배정된 학습지 문항. 문제에 학습지 안의 번호(index)·즐겨찾기 여부·출처가 더 붙는다.
 * 2026-10-14 백엔드 develop(6a0f8e7bb) 의 studentWorksheet/controller/response/StudentWorksheetProblemItemResponse.kt 로 확인.
 */
export const AssignedWorksheetProblemDetailSchema = AssignedWorksheetProblemWithEssaySchema.extend({
  problem: TaggedProblemSchema.extend({
    index: z.number(),
    favorite: z.boolean(),
    sourceType: ProblemSourceTypeSchema,
  }),
})
export type AssignedWorksheetProblemDetail = z.infer<typeof AssignedWorksheetProblemDetailSchema>

/** QR 학습지의 문제 목록. 배정 전이라 채점 기록은 비어 있다 */
export const WorksheetWithProblemsSchema = z.object({
  status: LearningStatusSchema,
  autoScorable: z.boolean(),
  worksheetProblems: z.array(AssignedWorksheetProblemSchema),
})
export type WorksheetWithProblems = z.infer<typeof WorksheetWithProblemsSchema>

// 학습지 조회가 싣는 문제는 유형 이름·태그가 붙고, 오답학습·CAS 가 넘기는 문제는 붙지 않을 수 있다
export type WorksheetScoringProblem = Problem &
  Partial<Pick<TaggedProblem, 'conceptName' | 'tagTop'>>

/** 학습지 문항 하나의 채점. 문항 번호·자동채점 여부·학년은 화면이 아는 학습지에서 온다 */
export type StudentWorksheetScoring = ScoringItem<
  { kind: 'WORKSHEET'; id: WorksheetProblem['id'] },
  WorksheetScoringProblem
>
